import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import { useEffect, useRef, useState } from 'react';
import { FieldPrivacyToggle } from './FieldPrivacyToggle';
import { useAccordionGroupSignals } from './edit-object/AccordionGroupContext';
import { useWizardAccordionVariant } from './edit-object/WizardAccordionStyleContext';

// Hi‑Fi design palette — applied when the surrounding
// WizardAccordionStyleContext provides variant="describe".
const DESCRIBE_HEADER_BG = '#fafafa';
const DESCRIBE_HEADER_BG_OPEN = '#f4f4f4';
const DESCRIBE_TITLE_COLOR = '#841617'; // ali-red-700
const DESCRIBE_TITLE_FONT_FAMILY =
  '"Libre Baskerville", "Times New Roman", serif';


interface WizardAccordionProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  'data-testid'?: string;
  id?: string;
  /** Optional field path for privacy toggle. If provided, renders a privacy toggle in the header. */
  privacyFieldPath?: string;
  /** If true, renders a static lock icon indicating the section is always private. */
  alwaysPrivate?: boolean;
  /** Optional adornment rendered beside the title (e.g. a help tooltip). */
  titleAdornment?: React.ReactNode;
  /** Optional summary chip shown only when collapsed (e.g. live field value preview). */
  summary?: React.ReactNode;
  /**
   * Optional summary rendered inline beside the title regardless of
   * expanded state. Used by the Getty Vocab panels to surface the
   * selected term (e.g. "painting (AAT 300033618)") at all times.
   */
  headerSummary?: React.ReactNode;
  /**
   * Optional controlled expansion state. When provided, the accordion
   * becomes controlled and `onExpandedChange` is called on toggle.
   */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const WizardAccordion = ({
  title,
  subtitle,
  children,
  defaultExpanded = false,
  'data-testid': testId,
  id,
  privacyFieldPath,
  alwaysPrivate = false,
  titleAdornment,
  summary,
  headerSummary,
  expanded: expandedProp,
  onExpandedChange,
}: WizardAccordionProps) => {
  const theme = useTheme();
  const variant = useWizardAccordionVariant();
  const isDescribe = variant === 'describe';
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const groupSignals = useAccordionGroupSignals();
  const lastExpandSignalRef = useRef(groupSignals?.expandSignal ?? 0);
  const lastCollapseSignalRef = useRef(groupSignals?.collapseSignal ?? 0);

  useEffect(() => {
    if (!groupSignals || isControlled) return;

    const expandSignalChanged = groupSignals.expandSignal !== lastExpandSignalRef.current;
    const collapseSignalChanged = groupSignals.collapseSignal !== lastCollapseSignalRef.current;

    if (expandSignalChanged) {
      lastExpandSignalRef.current = groupSignals.expandSignal;
      setInternalExpanded(true); // eslint-disable-line react-hooks/set-state-in-effect -- broadcast subscriber
    }
    if (collapseSignalChanged) {
      lastCollapseSignalRef.current = groupSignals.collapseSignal;
      setInternalExpanded(false);
    }
  }, [groupSignals, isControlled]);

  const handleChange = (_: unknown, isExpanded: boolean) => {
    if (!isControlled) {
      setInternalExpanded(isExpanded);
    }
    onExpandedChange?.(isExpanded);
  };

  return (
    <Accordion
      id={id}
      expanded={expanded}
      onChange={handleChange}
      data-testid={testId}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        '&:before': { display: 'none' },
        mb: 2,
        scrollMarginTop: '80px',
        ...(isDescribe && {
          '& .MuiAccordionSummary-root': {
            backgroundColor: DESCRIBE_HEADER_BG,
          },
          '&.Mui-expanded .MuiAccordionSummary-root': {
            backgroundColor: DESCRIBE_HEADER_BG_OPEN,
          },
        }),
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              component="h3"
              sx={{
                fontFamily: DESCRIBE_TITLE_FONT_FAMILY,
                fontSize: '1rem',
                fontWeight: 700,
                lineHeight: 1.3,
                color: DESCRIBE_TITLE_COLOR,
                m: 0,
              }}
            >
              {title}
            </Typography>
            {titleAdornment}
            {headerSummary !== undefined && headerSummary !== null && headerSummary !== '' && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: 1.5, fontWeight: 400 }}
                data-testid="wizard-accordion-header-summary"
              >
                {headerSummary}
              </Typography>
            )}
            {/*
             * The describe variant (Artwork Details page) hides per-section
             * Public/Private toggles; the page no longer surfaces section-level
             * visibility. Other steps continue to render the toggle.
             */}
            {privacyFieldPath && !isDescribe && (
              <FieldPrivacyToggle fieldPath={privacyFieldPath} size="small" />
            )}
            {alwaysPrivate && !isDescribe && (
              <Tooltip title="Always Private" arrow>
                <LockIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </Tooltip>
            )}
          </Box>
          {expanded && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
              {subtitle}
            </Typography>
          )}
          {!expanded && summary !== undefined && summary !== null && summary !== '' && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, fontStyle: 'italic' }}
            >
              {summary}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};
